import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { verifyPayment } from "../api/user";
import { CheckCircleIcon, ArrowRightIcon } from "@heroicons/react/24/solid";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedModal } from "../components/ui";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const stripeSessionId = searchParams.get("session_id");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!stripeSessionId) {
      return;
    }
    setIsLoading(true);
    verifyPayment(stripeSessionId)
      .then((res) => {
        // TODO: Optionally show in-app toast/notification for payment success (e.g. "Subscription activated!")
        // console.log(res);
      })
      .catch((err) => {
        // console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [stripeSessionId]);

  return (
    <AnimatedModal
      isOpen={true}
      backdropClassName="bg-black/60 backdrop-blur-sm"
      contentClassName="w-full max-w-md"
    >
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl border border-gray-100"
          >
            <div className="text-center">
              {/* Loading Spinner */}
              <div className="relative mx-auto mb-6">
                <div className="w-16 h-16 border-4 border-gray-200 border-t-primary rounded-full animate-spin mx-auto"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 bg-primary rounded-full animate-pulse"></div>
                </div>
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-3 font-dm-sans">
                Verifying Payment
              </h1>
              <p className="text-gray-600 text-base leading-relaxed">
                Please wait while we verify your payment and activate your
                subscription.
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl border border-gray-100"
          >
            <div className="text-center">
              {/* Success Icon with Animation */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  duration: 0.5,
                  delay: 0.2,
                  type: "spring",
                  stiffness: 200,
                  damping: 15,
                }}
                className="mx-auto mb-6 w-20 h-20 bg-green-100 rounded-full flex items-center justify-center"
              >
                <CheckCircleIcon className="w-12 h-12 text-green-600" />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
                className="text-3xl font-bold text-gray-900 mb-3 font-dm-sans"
              >
                Payment Successful!
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.3 }}
                className="text-gray-600 text-base leading-relaxed mb-8"
              >
                Your subscription has been activated successfully.
              </motion.p>

              <button
                className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                onClick={() => {
                  window.location.href = "/dashboard";
                }}
              >
                Continue to Dashboard
                <ArrowRightIcon className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AnimatedModal>
  );
};

export default PaymentSuccess;
